'use client';

import { cartHelpers } from '../helpers/cartHelpers';
import type { CartSaved, Attribute } from '@/types/manage.types';

/**
 * Cart calculation and validation result
 */
export interface CartCalculation {
  subtotal: number;
  total: number;
  itemCount: number;
  isEmpty: boolean;
  isValid: boolean;
  validationErrors: string[];
}

/**
 * Cart pricing breakdown
 */
export interface CartPricingBreakdown {
  subtotal: number;
  tax: number;
  delivery: number;
  discount: number;
  total: number;
}

/**
 * Cart service handling business logic for cart operations
 * Provides cart calculations, validations, and transformations
 */
export class CartService {
  /**
   * Calculate cart totals and validation
   */
  calculateCartSummary(items: CartSaved[]): CartCalculation {
    const subtotal = cartHelpers.calculateTotal(items);
    const itemCount = cartHelpers.getItemCount(items);
    const validation = cartHelpers.validateCartItems(items);

    return {
      subtotal,
      total: subtotal, // For now, total equals subtotal (no tax/delivery)
      itemCount,
      isEmpty: items.length === 0,
      isValid: validation.isValid,
      validationErrors: validation.errors,
    };
  }

  /**
   * Calculate detailed pricing breakdown
   * Future enhancement: add tax and delivery calculations
   */
  calculatePricingBreakdown(
    items: CartSaved[],
    options?: {
      taxRate?: number;
      deliveryFee?: number;
      discountAmount?: number;
    },
  ): CartPricingBreakdown {
    const subtotal = cartHelpers.calculateTotal(items);
    const taxRate = options?.taxRate || 0;
    const deliveryFee = options?.deliveryFee || 0;
    const discountAmount = options?.discountAmount || 0;

    const tax = subtotal * taxRate;
    const total = subtotal + tax + deliveryFee - discountAmount;

    return {
      subtotal,
      tax,
      delivery: deliveryFee,
      discount: discountAmount,
      total: Math.max(0, total), // Ensure total is never negative
    };
  }

  /**
   * Format price for display
   */
  formatPrice(amount: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  /**
   * Validate quantity value
   */
  validateQuantity(quantity: number): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    if (!Number.isInteger(quantity)) {
      errors.push('Quantity must be an integer');
    }
    if (quantity <= 0) {
      errors.push('Quantity must be greater than 0');
    }
    return { isValid: errors.length === 0, errors };
  }

  /**
   * Validate individual cart item
   */
  validateCartItem(item: CartSaved): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!item.variant_id || item.variant_id.trim() === '') {
      errors.push('Variant ID is required');
    }

    if (!item.sku || item.sku.trim() === '') {
      errors.push('SKU is required');
    }

    if (!item.quantity || item.quantity <= 0) {
      errors.push('Quantity must be greater than 0');
    }

    if (!item.in_cart) {
      errors.push('Cart metadata is required');
    } else {
      if (typeof item.in_cart.price !== 'number' || item.in_cart.price < 0) {
        errors.push('Valid price is required');
      }

      if (!item.in_cart.added_at || item.in_cart.added_at <= 0) {
        errors.push('Added timestamp is required');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Find item by variant ID
   */
  findItemByVariantId(items: CartSaved[], variantId: string): CartSaved | undefined {
    return cartHelpers.findItemByVariantId(items, variantId);
  }

  /**
   * Check if item exists in cart
   */
  hasItem(items: CartSaved[], variantId: string): boolean {
    return !!this.findItemByVariantId(items, variantId);
  }

  /**
   * Get quantity for specific variant
   */
  getItemQuantity(items: CartSaved[], variantId: string): number {
    const item = this.findItemByVariantId(items, variantId);
    return item?.quantity || 0;
  }

  /**
   * Calculate individual item total
   */
  calculateItemTotal(item: CartSaved): number {
    return (item.in_cart?.price || 0) * (item.quantity || 1);
  }

  /**
   * Group items by category or attributes
   */
  groupItemsByAttribute(items: CartSaved[], attributeKey: string): Record<string, CartSaved[]> {
    return items.reduce(
      (groups, item) => {
        const attribute = item.in_cart?.selected_attributes?.find((attr) => attr.key === attributeKey);
        const groupKey = attribute?.value || 'uncategorized';

        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(item);

        return groups;
      },
      {} as Record<string, CartSaved[]>,
    );
  }

  /**
   * Create cart summary for order submission
   */
  createOrderItems(items: CartSaved[]): Array<{
    variantId: string;
    sku: string;
    quantity: number;
    price: number;
    attributes: Attribute[];
    note?: string;
  }> {
    return items.map((item) => ({
      variantId: item.variant_id,
      sku: item.sku,
      quantity: item.quantity,
      price: item.in_cart?.price || 0,
      attributes: item.in_cart?.selected_attributes || [],
      note: item.note,
    }));
  }

  /**
   * Estimate order fulfillment time based on cart contents
   * Future enhancement: consider item complexity and current load
   */
  estimateFulfillmentTime(items: CartSaved[]): number {
    const baseTime = 30; // 30 minutes base
    const itemCount = cartHelpers.getItemCount(items);
    const additionalTime = Math.min(itemCount * 5, 60); // 5 min per item, max 60 min

    return baseTime + additionalTime;
  }

  /**
   * Check if cart meets minimum order requirements
   */
  meetsMinimumOrder(
    items: CartSaved[],
    minimumAmount: number = 0,
  ): {
    meets: boolean;
    currentAmount: number;
    shortfall: number;
  } {
    const currentAmount = cartHelpers.calculateTotal(items);
    const meets = currentAmount >= minimumAmount;
    const shortfall = meets ? 0 : minimumAmount - currentAmount;

    return {
      meets,
      currentAmount,
      shortfall,
    };
  }
}
